import React, { useState } from 'react';
import { X, Calendar, Clock, RotateCcw } from 'lucide-react';
import { HitApi } from '../../Api/ApiHit';
import { rescheduleBookingApi } from '../../constant/Constant';
import toast from 'react-hot-toast';

const TIME_SLOTS = [
  '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM',
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
  '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM',
  '08:00 PM',
];

const RescheduleModal = ({ booking, onClose, onSaved }) => {
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const handleReschedule = () => {
    if (!bookingDate) {
      toast.error('Please select a date');
      return;
    }
    if (!bookingTime) {
      toast.error('Please select a time');
      return;
    }

    setSaving(true);
    HitApi({
      _id: booking._id,
      bookingDate,
      bookingTime,
    }, rescheduleBookingApi).then((res) => {
      if (res?.statusCode === 200) {
        toast.success('Booking rescheduled successfully');
        onSaved();
      } else {
        toast.error(res?.error || 'Failed to reschedule booking');
      }
    }).catch(() => {
      toast.error('Failed to reschedule booking');
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <RotateCcw className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Reschedule Booking</h3>
              <p className="text-xs text-gray-500">{booking.bookingNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto">
          {/* Current Schedule */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-medium mb-2">Current Schedule</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{booking.bookingDate || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{booking.bookingTime || 'Not set'}</span>
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Client</p>
            <p className="text-sm font-medium text-gray-900">{booking.client?.name || '—'}</p>
            {booking.client?.phoneNumber && (
              <p className="text-xs text-gray-500">{booking.client.phoneNumber}</p>
            )}
          </div>

          {/* New Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              New Date
            </label>
            <input
              type="date"
              value={bookingDate}
              min={today}
              onChange={(e) => setBookingDate(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
            />
          </div>

          {/* New Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              New Time
            </label>
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {TIME_SLOTS.map((time) => (
                <button
                  key={time}
                  onClick={() => setBookingTime(time)}
                  className={`px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                    bookingTime === time
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>

          {/* Services Summary */}
          {booking.services?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 font-medium mb-2">Services ({booking.services.length})</p>
              {booking.services.map((s, i) => (
                <p key={i} className="text-xs text-gray-700">{s.name}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={saving || !bookingDate || !bookingTime}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RotateCcw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Rescheduling...' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
